import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { catchError, map, switchMap, take, tap } from 'rxjs/operators'
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { MbCaasService } from '../services/mb-caas/mb-caas.service'
import { Image, LinkData } from '../components/types'
import { LanguageService, Product, WindowRef } from '@spartacus/core'
import { CountryService } from '../../site-context/facade/country.service'
import { CmsEnvironmentService } from '../services/cms-environment.service'
import { CaasIdType } from '../enums/caas-id-types'
import { CmsComponentUtils } from '../components/utils/cms-component-utils'
import { BaseConverterService } from '../services/cms-converter/base-converter.service'
import { CmsConverterService } from '../services/cms-converter/cms-converter.service'
import { MbConstants } from '../../shared/mb-constants'
import { CmsProductService } from '../services/cms-product/cms-product.service'
import { CmsLinkType } from '../enums/cms-link-type'

@Injectable({
  providedIn: 'root'
})
export class MbNavigationService implements OnDestroy {

  navigation$ = new BehaviorSubject<NavigationNode[]>([])
  footer$ = new BehaviorSubject<NavigationNode>({} as any)
  seoRouteMap: any
  resolvedIdMap = new BehaviorSubject<NodeBreadcrumb[]>([])
  language: string | undefined = MbConstants.FALLBACK_LANGUAGE
  isFSPreview: boolean = false
  idMap: any
  private sub = new Subscription()

  constructor (private http: HttpClient,
               private mbCaasService: MbCaasService,
               private converterService: BaseConverterService,
               private languageService: LanguageService,
               private countryService: CountryService,
               private cmsEnvService: CmsEnvironmentService,
               private cmsProductService: CmsProductService,
               private winRef: WindowRef) {
  }

  init (): Observable<boolean> {
    this.sub.add(this.languageService.getActive().subscribe(isPreview => (this.language = isPreview)))
    this.sub.add(this.cmsEnvService.isFirstSpiritPreview$.subscribe(isPreview => (this.isFSPreview = isPreview)))
    this.sub.add(this.countryService.getActive().pipe(switchMap(() => this.createNavigation())).subscribe())
    if (this.winRef.isBrowser() && typeof this.winRef.nativeWindow !== 'undefined' && this.winRef.nativeWindow.Cypress) {
      const name: any = MbNavigationService.name
      this.winRef.nativeWindow[name] = this as any
    }
    return this.countryService.getActive().pipe(switchMap(() => this.createNavigation()), take(1))
  }

  createNavigation (): Observable<boolean> {
    return this.loadCmsNavigation().pipe(
      switchMap(data => this.assignNavigationProperties(data)),
      tap(data => this.createFooter(data)),
      map(data => this.mapHandbooks(data)),
      map(data => this.mapNavigation(data)),
      switchMap(navigation => this.createFlyoutTeasers(navigation)),
      tap(navigation => this.navigation$.next(navigation ?? [])),
      map(() => true))
  }

  assignNavigationProperties (navigationData: any): Observable<any> {
    if (!navigationData?.idMap) {
      return of(navigationData)
    }
    return of(navigationData).pipe(
      // Filter all nodes containing a product_id of an unavailable product
      switchMap((navigation: any) => this.filterNodesWithProductIds(navigation)),
      switchMap((navigation: any) => this.filterNodesWithCategoryIds(navigation)),
      map((navigation: any) => {
        const resolvedIdMap = Object.keys(navigation?.idMap ?? {}).map(id => navigation?.idMap?.[id]) ?? []
        this.resolvedIdMap.next(resolvedIdMap)
        this.idMap = navigation?.idMap ?? {}
        this.seoRouteMap = navigation?.seoRouteMap ?? {}
        return navigation
      })
    )
  }

  createFlyoutTeasers (navigation: NavigationNode[]): Observable<NavigationNode[]> {
    return this.loadFlyoutPages(navigation)
      .pipe(
        switchMap(teaserPages => this.replaceFlyoutLinkUrls(teaserPages)),
        map(flyoutPages => this.addFlyoutTeasersToNavigation(flyoutPages, navigation)),
      )
  }

  addFlyoutTeasersToNavigation (flyoutPages: any[], navigation: NavigationNode[]): NavigationNode[] {
    if (navigation?.[0]?.children?.[0]?.children?.length) {
      navigation[0].children[0].children = navigation[0]?.children?.[0]?.children?.map((node: any) => {
        const page = flyoutPages?.find(flyoutPage => flyoutPage?.identifier === node.id)
        if (node?.id === page?.identifier) {
          node.teasers = this.convertTeasers(page?.page)?.teasers ?? []
        }
        return node
      })
    }
    return navigation
  }

  replaceFlyoutLinkUrls (teaserPages: any[] = []): Observable<any> {
    const teasers = teaserPages.flatMap(t => t?.page?.formData?.st_teaser?.value ?? [])
    return this.loadTeaserLinkPages(teasers).pipe(
      map(teaserLinkPages => teaserPages.map((teaserPage: any) => {
          if (teaserPage?.page?.formData?.st_teaser?.value) {
            teaserPage.page.formData.st_teaser.value = teaserPage.page.formData.st_teaser.value.map((teaser: any) => {
              const linkPage = teaserLinkPages.find((tp: any) => tp.identifier ===
                teaser?.formData?.st_link?.value?.formData?.lt_page?.value?.identifier)
              if (teaser?.formData?.st_link?.value) {
                teaser.formData.st_link.value.url = this.getTeaserLinkUrl(teaser.formData.st_link.value, linkPage)
              }
              return teaser
            })
          }
          return teaserPage
        })
      )
    )
  }

  getTeaserLinkUrl (link: any, linkPage: any): string {
    switch (link?.template?.uid) {
      case 'product_link':
        return link?.formData?.lt_product?.value?.[0]?.identifier?.split('/')?.[1] ?? ''
      case 'document_link':
        return link?.formData?.lt_reference?.value?.[0]?.value?.target?.baseUrl ?? ''
      case 'url':
        return link?.formData?.lt_url?.value ?? ''
      default:
        return linkPage?.page?.formData?.pt_product_id?.value?.[0]?.identifier?.split('/')?.[1]
          || linkPage?.page?.formData?.pt_seoUrl?.value
          || linkPage?.page?.uid
    }
  }

  loadTeaserLinkPages (teasers: any[]): Observable<any> {
    const keys = [
      { key: 'page.template.uid', operator: 1 },
      { key: 'page.uid', operator: 1 },
      { key: 'page.formData.pt_product_id.value.identifier', operator: 1 },
      { key: 'page.formData.pt_seoUrl.value', operator: 1 },
      { key: 'identifier', operator: 1 }
    ] as any
    const ids = teasers.map((teaser: any) => teaser?.formData?.st_link?.value?.formData?.lt_page?.value?.identifier ?? '')
    const page = { page: 1, pageSize: ids.length }
    return this.mbCaasService.getByIds(ids, CaasIdType.PAGEID, keys, page)
  }

  loadFlyoutPages (navigation: NavigationNode[] | undefined): Observable<NavigationNode[]> {
    const productCategoriesIds = navigation?.[0]?.children?.[0]?.children?.map(node => node.id) ?? []
    const teaserKeys = [
      { key: 'page.formData.st_teaser', operator: 1 },
      { key: 'identifier', operator: 1 }
    ] as any
    const pagination = { page: 1, pageSize: productCategoriesIds.length }
    return this.mbCaasService.getByIds(productCategoriesIds, CaasIdType.PAGEID, teaserKeys, pagination)
  }

  getSeoUrlOfPageRef (refId: string): string {
    return this.resolvedIdMap.value.filter(entry => entry.id === refId)?.[0]?.customData?.pt_seoUrl ?? ''
  }

  ngOnDestroy (): void {
    this.sub?.unsubscribe()
  }

  private mapHandbooks (navigation: any): void {
    const resources = navigation?.structure?.filter((category: any) =>
      navigation.idMap?.[category.id]?.label?.toLowerCase()?.includes('resources')) ?? []
    const handBookFirstLevel = resources?.[0]?.children?.filter((resource: any) =>
      navigation?.idMap?.[resource.id]?.label?.toLowerCase()?.includes('handbook'))?.[0]?.children ?? []
    handBookFirstLevel.forEach((firstLevel: any) => {
      firstLevel?.children?.forEach((secondLevel: StructuralNode) => {
        const books = this.resolvedIdMap?.value?.filter((item: any) => item?.parentIds?.[3] === secondLevel.id && !!item.customData) ?? []
        this.mapHandbookToStructure(secondLevel, books)
      })
    })
    return navigation
  }

  private mapHandbookToStructure (secondLevel: StructuralNode, books: any[]): void {
    books.forEach((book: any) => {
      if (!secondLevel?.children?.length) {
        secondLevel?.children?.push({ id: book.id, children: [] })
      }
    })
  }

  private createFooter (navigation: any): void {
    this.footer$.next(navigation?.structure?.filter((category: any) => navigation?.idMap?.[category.id]?.label === 'Footer')
      .map((category: any) => this.mapNode(navigation?.idMap?.[category.id] ?? {}, category))[0] ?? {})
  }

  private mapNavigation (navigation: any): NavigationNode[] {
    return navigation?.structure?.filter((category: any) => navigation?.idMap?.[category?.id]?.label !== 'Footer')
      .map((category: any) => this.mapNode(navigation?.idMap?.[category?.id] ?? {}, category))
  }

  private loadCmsNavigation (): Observable<any> {
    const url = this.isFSPreview
      ? environment.navigationServicePreviewUrl
      : this.cmsEnvService.sanitizeSsr(environment.navigationServiceReleaseUrl)
    return this.countryService.getActive().pipe(
      switchMap(activeCountry => this.http.get(url, {
          params: {
            depth: '10',
            format: 'caas',
            language: `${this.language}_${activeCountry}`
          },
        })
      ),
      catchError(error => this.handleError(error))
    )
  }

  private mapNode (node: NodeBreadcrumb, category?: any | null): NavigationNode {
    const page = this.getPageByNode(node)
    let label = page.customData?.pt_pageTitle || node.label || ''
    if (label === 'MACS Handbook' && node.parentIds.length === 2) {
      label = node.label
    }
    const url = page.caasDocumentId === null ? page.contentReference : (page.customData?.product_id?.split('/')?.[1] || page.customData?.pt_seoUrl || node.customData?.pt_seoUrl || '')
    const isExternalUrl = page.caasDocumentId === null

    return {
      id: page.id || node.id,
      label: label === 'empty' ? '' : label,
      pageTitle: page.customData?.pt_pageTitle ?? '',
      color: page.customData?.pt_pageMainColor || node.customData?.pt_pageMainColor || 'corporate-orange',
      url: url,
      isExternalUrl: isExternalUrl,
      productId: page.customData?.product_id?.split('/')?.[1] || '',
      seoRoute: page.seoRoute || node.seoRoute,
      customData: {
        /* eslint-disable @typescript-eslint/naming-convention */
        pt_hideBreadcrumb: page.customData?.pt_hideBreadcrumb === 'true' || false,
        pt_shortenBreadcrumb: page.customData?.pt_shortenBreadcrumb === 'true' || false,
        /* eslint-enable @typescript-eslint/naming-convention */
      },
      parentIds: page.parentIds || node.parentIds,
      children: category?.children?.map((child: any) => this.mapNode(this.idMap?.[child.id] ?? {}, child))
    }
  }

  private getPageByNode (node: any): any {
    return this.resolvedIdMap.value?.filter(n =>
      n.caasDocumentId === node?.caasDocumentId
      && n.parentIds?.length === node?.parentIds.length + 1
      && !!n.customData
    )[0] ?? {}
  }

  private filterNodesWithProductIds (navigationData: any): Observable<any> {
    const resolvedIdMap = Object.keys(navigationData.idMap).map((key: string) => navigationData.idMap[key] ?? {})
    const nodesWithProductId = resolvedIdMap.filter((node: any) => node?.customData?.product_id?.length)
    const productIds = nodesWithProductId.map((node: any) => node.customData.product_id.split('/').pop())
    return this.cmsProductService.getProductsByIds(productIds).pipe(
      map(products => {
        if (!products?.products?.length) {
          return navigationData
        }
        const fetchedProductIds = products.products.map((product: Product) => product?.variantOptions?.[0]?.code)
        nodesWithProductId.filter((node: any) => !fetchedProductIds.includes(node.customData.product_id.split('/').pop()))
          // remove all nodes from idMap where no product has been found
          .forEach((node: any) => delete navigationData.idMap[node.id])
        return navigationData
      })
    )
  }

  private filterNodesWithCategoryIds (navigationData: any): Observable<any> {
    const resolvedIdMap = Object.keys(navigationData.idMap).map((key: string) => navigationData.idMap[key] ?? {})
    const nodesWithCategoryIds = resolvedIdMap.filter((node: any) => node?.customData?.pt_category_id?.length)
    return this.cmsProductService.getCategoriesWithAvailableProducts().pipe(
      map(categories => {
        nodesWithCategoryIds.forEach(node => {
          // get results for node
          const nodeCategoryIds = node.customData.pt_category_id.split(',').map((id: string) => id.split('/').pop())
          if (nodeCategoryIds.every((id: string) => (categories ?? [])?.indexOf(id) === -1)) {
            delete navigationData.idMap[node.id]
          }
        })
        return navigationData
      })
    )
  }

  private handleError (error: HttpErrorResponse): Observable<NavigationNode[]> {
    const status = error?.error?.status || error?.status || ''
    const message = error?.error?.error?.message || error?.statusText || ''
    console.error('Can\'t load navigation: ', `status: ${status} - message: ${message}`)
    return of([])
  }

  private convertTeasers (source: any): any {
    source = this.converterService.parseComponentData(source as any ?? {}) as any
    if (!source?.formData?.st_teaser?.value) {
      return source
    }
    source.teasers = source?.formData?.st_teaser?.value?.map((teaser: any) => {
        const link = teaser?.formData?.st_link?.value ?? {}
        const isVisible = CmsComponentUtils.isComponentVisible({
          hide: teaser?.formData?.st_hide_component?.value ?? false,
          hideBefore: teaser?.formData?.st_hide_before_date?.value ?? '',
          hideAfter: teaser?.formData?.st_hide_after_date?.value ?? ''
        }, this.isFSPreview)
        return {
          caption: teaser?.formData?.st_caption?.value ?? '',
          dataEventAction: teaser?.formData?.st_caption?.value?.replaceAll(/<.*?>/g, ''),
          isVisible,
          image: CmsConverterService.mapImage(teaser?.formData?.st_image_reference, teaser?.formData?.st_lazy_load_image?.value,
            teaser?.formData?.st_alt_text?.value),
          link: {
            anchor: link.formData?.lt_anchor?.value ?? '',
            url: link.url ?? '',
            text: (({ value = '', name = '' }) => ({ value, name }))(link.formData?.lt_text ?? {}),
            target: link.formData?.lt_target?.value?.identifier ?? '_self',
            tooltip: link.formData?.lt_tooltip_text?.value ?? '',
            uid: teaser.formData?.lt_page?.value?.uid ?? '',
          }
        }
      }
    ) as any ?? []
    return source
  }

}

export interface StructuralNode {
  id: string;
  children?: StructuralNode[];
}

export interface NavigationNode {
  id: string;
  label: string;
  pageTitle?: string;
  url: string;
  isExternalUrl?: boolean;
  color?: string;
  seoRoute?: string;
  parentIds?: string[];
  children?: NavigationNode[];
  teasers?: FlyoutTeaserData[];
  productId?: string;
  customData: {
    /* eslint-disable @typescript-eslint/naming-convention */
    pt_hideBreadcrumb?: boolean;
    pt_shortenBreadcrumb?: boolean;
    /* eslint-enable @typescript-eslint/naming-convention */
  };
}

export interface FooterNode extends NavigationNode {
  image?: Image;
  cmsLinkType?: CmsLinkType;
  hasIconLinks?: boolean;
  children?: FooterNode[] | any[];
}

export interface FlyoutTeaserData {
  image: Image;
  isVisible: boolean;
  caption: string;
  dataEventAction: string;
  link: LinkData;
}

export interface NodeBreadcrumb {
  caasDocumentId: string;
  contentReference: string;
  label: string;
  parentIds: string[];
  id: string;
  customData?: {
    /* eslint-disable @typescript-eslint/naming-convention */
    pt_breadcrumb?: string;
    pt_pageMainColor?: string;
    pt_pageTitle?: string;
    pt_seoUrl?: string;
    pt_hideBreadcrumb?: boolean;
    pt_shortenBreadcrumb?: boolean;
    /* eslint-enable @typescript-eslint/naming-convention */
  };
  seoRoute: string;
  permissions: string | null;
  seoRouteRegex: string | null;
  url?: string;
  color?: string;
  link?: string;
}
